import dayjs from 'dayjs';

import { paths } from 'src/routes/paths';

import { toDayjs } from './myndighetsdatum';

import type { AttGöraPost } from './AttGoraTyper';
import type { IEgenkontrolluppgift } from '../fastighet/egenkontroll/EgenkontrollService';

// ----------------------------------------------------------------------

/**
 * Översätter en rad ur egenkontrollregistret till en rad i att göra-listan.
 *
 * Hit hör bara det som ska göras. Statusen räknas av backend ur protokollen, och `kräverÅtgärd`
 * är bredare än den — en kontroll kan vara aktuell och ändå ha en anmärkning vars frist gått ut,
 * eller ett protokoll som aldrig skickades till byggnadsnämnden.
 *
 * En kontroll som aldrig gjorts har ingen förfallodag. Den läggs på dagens datum, inte längst
 * fram i listan: en OVK som aldrig utförts är försenad, inte oplanerad.
 */
export function tillEgenkontrollpost(uppgift: IEgenkontrolluppgift): AttGöraPost | null {
  if (!uppgift?.kontrollobjektId) return null;
  if (!uppgift.kräverÅtgärd && uppgift.status === 'AKTUELL') return null;

  const förfallodag = toDayjs(uppgift.förfallodag) ?? dayjs().startOf('day');

  return {
    id: `egenkontroll-${uppgift.kontrollobjektId}`,
    titel: titel(uppgift),
    detalj: detalj(uppgift),
    källa: 'EGENKONTROLL',
    avsändare: uppgift.typEtikett,
    förfallodag,
    lagrum: uppgift.lagrum,
    länk: paths.dashboard.fastighet.egenkontroll,
    länkText: 'Till egenkontrollen',
    internLänk: true,
    kräverÅtgärd: uppgift.kräverÅtgärd,
  };
}

function titel(uppgift: IEgenkontrolluppgift): string {
  switch (uppgift.status) {
    case 'UNDERKÄND':
      return `${uppgift.benämning} är underkänd`;
    case 'FÖRFALLEN':
      return `${uppgift.benämning} har gått ut`;
    case 'ALDRIG_KONTROLLERAD':
      return `${uppgift.benämning} är inte kontrollerad`;
    default:
      return `Boka ${uppgift.benämning.toLowerCase()}`;
  }
}

/**
 * Andraraden säger vad som behöver göras, inte vad som har hänt.
 *
 * Flera skäl kan gälla samtidigt — en förfallen kontroll med två öppna anmärkningar och ett
 * orapporterat protokoll — och då hör de ihop i en mening i stället för i tre poster.
 */
function detalj(uppgift: IEgenkontrolluppgift): string | undefined {
  const delar: string[] = [];

  if (uppgift.status === 'UNDERKÄND') {
    delar.push('Senaste kontrollen blev underkänd.');
  }
  if (uppgift.mätvärdeÖverträderGräns) {
    delar.push('Uppmätt värde ligger utanför referensvärdet.');
  }
  if (uppgift.anmärkningarMedPasseradFrist > 0) {
    delar.push(
      uppgift.anmärkningarMedPasseradFrist === 1
        ? 'En anmärkning har passerat sin åtgärdsfrist.'
        : `${uppgift.anmärkningarMedPasseradFrist} anmärkningar har passerat sin åtgärdsfrist.`
    );
  } else if (uppgift.öppnaAnmärkningar > 0) {
    delar.push(
      uppgift.öppnaAnmärkningar === 1
        ? 'En öppen anmärkning.'
        : `${uppgift.öppnaAnmärkningar} öppna anmärkningar.`
    );
  }
  if (uppgift.väntarPåRapportering) {
    delar.push('Protokollet är inte rapporterat vidare.');
  }
  if (uppgift.status === 'ALDRIG_KONTROLLERAD') {
    delar.push('Ingen kontroll är registrerad.');
  }

  return delar.length > 0 ? delar.join(' ') : undefined;
}
